import { AddIcon } from '@chakra-ui/icons';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Heading,
  Skeleton,
  Stack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  UserRole,
  useGetIndividualDocumentsQuery,
  useGetIndividualIdentityQuery,
} from '@blockpulse3/graphql/hooks';
import {
  AddIdentityDocuments,
  ErrorQueryCard,
  LinkButton,
  useGetKycDocuments,
} from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { DocumentTable } from '../DocumentTable';

type Props = {
  backHref?: string;
};

/**
 * DocumentsView.
 *
 * @returns {JSX.Element}
 */
export function UserDocumentsView({ backHref }: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nDocuments = useTranslations('DocumentsView');

  const [isAddingDocuments, setIsAddingDocuments] = useState<boolean>(false);

  const { user: currentUser } = useUser();
  const { individualIdentityId = '' } = useParams();

  const { data: individualData, loading: loadingIndividual } = useGetIndividualIdentityQuery({
    variables: {
      individualIdentityId,
    },
    skip: !individualIdentityId,
  });

  const individualIdentity = individualIdentityId
    ? individualData?.individualIdentity
    : currentUser?.individualIdentity;

  const {
    data: documentsData,
    loading: loadingDocuments,
    error: documentsError,
    refetch,
  } = useGetIndividualDocumentsQuery({
    variables: {
      individualIdentityId: individualIdentity?.id || '',
    },
    skip: !individualIdentity?.id,
  });

  const [getKycDocuments] = useGetKycDocuments();

  const identityId = individualIdentity?.identity?.id;

  if (loadingIndividual || loadingDocuments) {
    return (
      <Stack spacing="6">
        {backHref && <LinkButton label={i18nGeneral('Back')} route={backHref} />}
        <Heading>{i18nDocuments('Title')}</Heading>
        <Skeleton>
          <Card h="300px" />
        </Skeleton>
      </Stack>
    );
  }

  if (documentsError || !identityId) {
    return (
      <Stack spacing="6">
        {backHref && <LinkButton label={i18nGeneral('Back')} route={backHref} />}
        <Heading>{i18nDocuments('Title')}</Heading>
        <ErrorQueryCard h="300px" />
      </Stack>
    );
  }

  const documents = documentsData?.getIndividualDocuments?.map(({ document }) => document) || [];
  const hasAllDocuments = documents.length >= getKycDocuments().length;
  const isUploadAuthorized = currentUser?.roles.some((role) =>
    [UserRole.COMPLIANCE_OFFICER, UserRole.SUPERADMIN].includes(role),
  );

  return (
    <Stack spacing="6">
      {backHref && <LinkButton label={i18nGeneral('Back')} route={backHref} />}
      <Heading>{i18nDocuments('Title')}</Heading>
      <Card>
        <CardHeader alignItems="center" as={HStack} justifyContent="space-between">
          <Heading size="md">{i18nDocuments('CardTitleUser')}</Heading>
          {!hasAllDocuments && isUploadAuthorized && (
            <Button
              leftIcon={<AddIcon />}
              size="sm"
              variant="secondary"
              w="fit-content"
              onClick={(): void => setIsAddingDocuments((val) => !val)}
            >
              {i18nGeneral('AddDocument')}
            </Button>
          )}
        </CardHeader>
        <CardBody>
          {isAddingDocuments && (
            <Stack pb="4">
              <AddIdentityDocuments
                identityId={identityId}
                onlyIdentityDocuments={identityId ? false : true}
                onUploadCompleted={refetch}
              />
            </Stack>
          )}
          <DocumentTable identityId={identityId} values={documents} onDeleteCompleted={refetch} />
        </CardBody>
      </Card>
    </Stack>
  );
}

export type UserDocumentsViewProps = Props;
